import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@clerk/react';
import { ClerkMetadataKey } from '@this+that/utils';

import { useCustomToast } from '../../../hooks';
import { updateUnsafeMetadata } from '../../../utils/clerkMetadata/updateUnsafeMetadata';
import { DEFAULT_INBOX_TABS_CONFIG, InboxTabConfig } from '../types';

const I18N_KEYS = {
  SAVING: 'inbox.settings.saving',
  SAVE_SUCCESS: 'inbox.settings.saveSuccess',
  SAVE_ERROR: 'inbox.settings.saveError',
} as const;

function resolveInitialTabs(
  unsafeMetadata: Record<string, unknown>,
): InboxTabConfig[] {
  const stored = unsafeMetadata[ClerkMetadataKey.INBOX_TABS_CONFIG];
  if (Array.isArray(stored) && stored.length > 0) {
    return stored as InboxTabConfig[];
  }
  return DEFAULT_INBOX_TABS_CONFIG;
}

export function useInboxTabsConfig() {
  const { user } = useUser();
  const { t } = useTranslation();
  const { promiseToast } = useCustomToast();
  const [isSaving, setIsSaving] = useState(false);

  const selectedTabs = resolveInitialTabs(user?.unsafeMetadata ?? {});

  const setSelectedTabs = async (tabs: InboxTabConfig[]) => {
    setIsSaving(true);
    try {
      if (!user) {
        return;
      }

      const updatePromise = updateUnsafeMetadata(user, {
        [ClerkMetadataKey.INBOX_TABS_CONFIG]: tabs,
      });
      await promiseToast(
        updatePromise,
        t(I18N_KEYS.SAVING),
        t(I18N_KEYS.SAVE_SUCCESS),
        t(I18N_KEYS.SAVE_ERROR),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return { selectedTabs, setSelectedTabs, isSaving };
}
